import { useEffect, useState, type ReactNode } from 'react'
import { Spinner } from '@/shared/ui/spinner'
import {
  installAuthInterceptors,
  refresh,
  setOnUnauthenticated,
} from '@/features/auth/services/refresh-controller'
import { authStore } from '@/features/auth/services/auth-store'
import {
  registerTabSession,
  requestSessionInheritance,
} from '@/features/auth/services/tab-session'
import {
  broadcastAuth,
  subscribeAuth,
} from '@/features/auth/services/auth-broadcast'
import { authApi } from '@/features/auth/api/auth.api'
import { env } from '@/shared/config/env'
import { Pages } from '@/shared/config/pages'

interface BootGateProps {
  children: ReactNode
}

function computeInitialReady(): boolean {
  if (env.mock || !env.authRefreshEnabled) return true
  if (authStore.hasSessionMarker()) return false
  if (authStore.getPersistPreference() === '0') return false
  return true
}

export function BootGate({ children }: BootGateProps) {
  const [ready, setReady] = useState(computeInitialReady)

  useEffect(() => {
    installAuthInterceptors()
    const unregisterTabSession = registerTabSession()

    const handleLocalLogout = (broadcast: boolean) => {
      authStore.clear()
      authStore.clearSessionMarkers()
      if (broadcast) broadcastAuth({ type: 'logout' })
      if (typeof window !== 'undefined') {
        const target = `#${Pages.LOGIN}`
        if (window.location.hash !== target) {
          window.location.hash = Pages.LOGIN
        }
      }
    }

    setOnUnauthenticated(() => handleLocalLogout(true))

    const unsubscribe = subscribeAuth(msg => {
      if (msg.type === 'logout') {
        handleLocalLogout(false)
      }
    })

    const baseCleanup = () => {
      unsubscribe()
      unregisterTabSession()
    }

    if (env.mock || !env.authRefreshEnabled) return baseCleanup

    let cancelled = false

    const rehydrate = async () => {
      try {
        await refresh()
      } catch {
        authStore.clear()
        authStore.clearSessionMarkers()
      }
    }

    const boot = async () => {
      if (authStore.hasSessionMarker()) {
        await rehydrate()
        return
      }

      if (authStore.getPersistPreference() === '0') {
        const inherited = await requestSessionInheritance()
        if (inherited) {
          await rehydrate()
        } else {
          try {
            await authApi.logout()
          } catch {
            /* intentionally ignored */
          }
          authStore.clear()
          authStore.clearSessionMarkers()
        }
      }
    }

    boot().finally(() => {
      if (!cancelled) setReady(true)
    })

    return () => {
      cancelled = true
      baseCleanup()
    }
  }, [])

  if (!ready) {
    return (
      <div className="flex min-h-svh w-full items-center justify-center">
        <Spinner />
      </div>
    )
  }

  return <>{children}</>
}
